import { API_BASE_URL, authFetch, authFetchJson } from '../apiClient.ts';
import { downloadBlob } from '../utils/helperUtils.ts';

export type ImportKind = 'contacts' | 'companies' | 'opportunities';

export const IMPORT_KINDS: {
  kind: ImportKind;
  label: string;
  one: string;
  many: string;
  // What the preview's second column names: the person, for an opportunity
  rowLabel: string;
  listPath: string;
}[] = [
  {
    kind: 'contacts',
    label: 'Contacts',
    one: 'contact',
    many: 'contacts',
    rowLabel: 'Contact',
    listPath: '/contacts'
  },
  {
    kind: 'companies',
    label: 'Companies',
    one: 'company',
    many: 'companies',
    rowLabel: 'Company',
    listPath: '/companies'
  },
  {
    kind: 'opportunities',
    label: 'Opportunities',
    one: 'opportunity',
    many: 'opportunities',
    rowLabel: 'Contact',
    listPath: '/opportunities'
  }
];

export type ImportKindInfo = (typeof IMPORT_KINDS)[number];

export const importKindOf = (value: string | undefined) =>
  IMPORT_KINDS.find((entry) => entry.kind === value) ?? IMPORT_KINDS[0];

// The screen sits under Settings, so the sidebar shows where it lives.
export const importPath = (kind: ImportKind) => `/settings/import/${kind}`;

export type ImportGuide = {
  kind: ImportKind;
  rowRule: string;
  fields: {
    key: string;
    label: string;
    description: string;
    // Left out of the template, as another way to give a column it has
    alternative: boolean;
    // The tenant's own values, where a column has to name one
    options?: string[];
  }[];
};

export type ImportOutcome = 'create' | 'update' | 'unchanged' | 'duplicate' | 'error';

export type ImportRow = {
  line: number;
  label: string;
  detail: string | null;
  outcome: ImportOutcome;
  match: { id: number; label: string } | null;
  fills: string[];
  error: string | null;
  warnings: string[];
  // The row as the file has it, only on rows that were not imported
  cells?: string[];
};

// Values the file uses that name none of the tenant's own, and what each
// becomes: `chosen` is an option id, or null for the fallback.
export type ValueChoice = {
  field: string;
  label: string;
  options: { id: string; name: string }[];
  fallback: string;
  values: { value: string; rows: number; chosen: string | null }[];
};

export type ColumnMapping = Record<string, string>;
// Field, then the file's value, then the option picked for it
export type PickedValues = Record<string, Record<string, string>>;

export type ImportResult = {
  kind: ImportKind;
  dryRun: boolean;
  headers: string[];
  mapping: ColumnMapping;
  missing: string | null;
  warnings: string[];
  choices: ValueChoice[];
  rows: ImportRow[];
  summary: Record<ImportOutcome, number> & {
    rows: number;
    newCompanies: number;
    newContacts: number;
    newIndustries: number;
  };
};

export const fetchImportGuide = async (kind: ImportKind): Promise<ImportGuide> => {
  const { json } = await authFetchJson(`${API_BASE_URL}/data-import/${kind}`);
  return json as ImportGuide;
};

// Headers the importer reads and two example rows made from this tenant's own
// statuses, stages and products.
export const downloadTemplate = async (kind: ImportKind) => {
  const response = await authFetch(`${API_BASE_URL}/data-import/${kind}/template`);
  if (!response.ok) {
    throw new Error(`Download failed with status ${response.status}`);
  }
  downloadBlob(await response.blob(), `forge-${kind}-template.csv`);
};

// dryRun previews the file without writing anything.
export const runImport = async (
  kind: ImportKind,
  file: File,
  options: { dryRun: boolean; mapping?: ColumnMapping; values?: PickedValues }
): Promise<ImportResult> => {
  const body = new FormData();
  body.append('file', file);
  if (options.mapping) body.append('mapping', JSON.stringify(options.mapping));
  if (options.values && Object.keys(options.values).length) {
    body.append('values', JSON.stringify(options.values));
  }
  const { json } = await authFetchJson(
    `${API_BASE_URL}/data-import/${kind}?dryRun=${options.dryRun}`,
    { method: 'POST', body }
  );
  return json as ImportResult;
};

const csvCell = (value: string) =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

// The rows Forge could not import, as the file had them, with a Problem column
// saying why: fix them and upload this file again.
export const downloadSkippedRows = (result: ImportResult, filename: string) => {
  const skipped = result.rows.filter((row) => row.outcome === 'error' && row.cells);
  const width = result.headers.length;
  const lines = [
    [...result.headers, 'Problem'],
    ...skipped.map((row) => {
      const cells = [...(row.cells ?? [])];
      while (cells.length < width) cells.push('');
      return [...cells, row.error ?? ''];
    })
  ];
  const text = lines.map((cells) => cells.map(csvCell).join(',')).join('\r\n');
  // The byte order mark tells Excel the file is UTF-8
  downloadBlob(new Blob(['﻿', text], { type: 'text/csv' }), filename);
};

export const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

// What an import would do, for the preview.
export const summaryLine = (result: ImportResult, kind: ImportKindInfo) => {
  const { summary } = result;
  const parts = [
    `${summary.create.toLocaleString()} to add`,
    summary.update ? `${summary.update.toLocaleString()} to fill in` : null,
    summary.unchanged ? `${summary.unchanged.toLocaleString()} already in Forge` : null,
    summary.duplicate ? `${summary.duplicate.toLocaleString()} repeated in the file` : null,
    summary.error ? `${summary.error.toLocaleString()} skipped` : null
  ].filter(Boolean);
  const extras = [
    summary.newContacts ? `${plural(summary.newContacts, 'new contact', 'new contacts')}` : null,
    summary.newCompanies ? `${plural(summary.newCompanies, 'new company', 'new companies')}` : null,
    summary.newIndustries
      ? `${plural(summary.newIndustries, 'new industry', 'new industries')}`
      : null
  ].filter(Boolean);
  const also = extras.length
    ? ` ${kind.kind === 'contacts' ? 'Adds' : 'Also adds'} ${extras.join(', ')}.`
    : '';
  return `${parts.join(', ')}.${also}`;
};

// What an import did, once it has.
export const doneLine = (result: ImportResult, kind: ImportKindInfo) => {
  const { summary } = result;
  const parts = [
    summary.create ? `added ${plural(summary.create, kind.one, kind.many)}` : null,
    summary.update ? `filled in ${summary.update.toLocaleString()}` : null,
    summary.unchanged ? `${summary.unchanged.toLocaleString()} already in Forge` : null,
    summary.error ? `skipped ${summary.error.toLocaleString()}` : null
  ].filter(Boolean);
  const extras = [
    summary.newContacts ? plural(summary.newContacts, 'contact', 'contacts') : null,
    summary.newCompanies ? plural(summary.newCompanies, 'company', 'companies') : null,
    summary.newIndustries ? plural(summary.newIndustries, 'industry', 'industries') : null
  ].filter(Boolean);
  const text = parts.join(', ');
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}.${extras.length ? ` New: ${extras.join(', ')}.` : ''}`;
};
